import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@react/i18n';
import { Spinner } from '../checkout/sections/ui';
import { Branch } from './types';
import { DeliveryPaymentApi } from './api/deliveryPaymentApi';
import { loadMarkerClusterer, useGoogleMaps } from './useGoogleMaps';

interface Props {
    title: string;
    /** mezinárodní trh: pobočky konkrétní dopravy; CZ/SK skupina: undefined */
    deliveryId?: number;
    selectedBranchId: number | null;
    api: DeliveryPaymentApi;
    googleMapsApiKey: string | null;
    language: string;
    /** ISO2 zvolené země — omezuje Places našeptávač */
    countryCode: string | null;
    /** logo dopravy pro mezinárodní pobočky (CZ/SK mají logo per pobočka) */
    fallbackLogoUrl?: string | null;
    confirming: boolean;
    onConfirm: (branch: Branch) => void;
    onClose: () => void;
}

interface LatLng {
    lat: number;
    lng: number;
}

/** Pod tímto zoomem (celá zem / kraj) je poboček moc — seznam se nevypisuje, jen mapa + výzva. */
const VIEWPORT_LIST_MIN_ZOOM = 11;
/** Okruh geo hledání — parita s legacy (MAX_DISTANCE_IN_KM na serveru) */
const GEO_RADIUS_KM = 10;
/** Limity vypsaného seznamu — DOM s tisíci řádky se renderuje dlouho */
const GEO_LIST_LIMIT = 99;
const LIST_LIMIT = 400;
/** ikony markerů — stejné jako legacy googleMap.js */
const MARKER_ICON = '/images/icons/png/image-ico-red.png';
const MARKER_ICON_SELECTED = '/images/icons/png/image-ico-green.png';
/** zoom po výběru pobočky / adresy — parita s legacy (setZoom(15)) */
const SELECT_ZOOM = 15;

function distanceKm(a: LatLng, b: LatLng): number {
    const rad = Math.PI / 180;
    const x = Math.sin(a.lat * rad) * Math.sin(b.lat * rad)
        + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.cos((a.lng - b.lng) * rad);

    return Math.acos(Math.min(1, x)) * 6371.009;
}

/** porovnávání bez diakritiky — „bansk" i „bánsk" najde „Banská" */
function fold(s: string): string {
    return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** co seznam vlevo právě odráží: fulltext / okolí adresy / výřez mapy */
type ListSource = 'text' | 'geo' | 'map' | null;

/**
 * Modal výběru pobočky. Pobočky se stáhnou jedním requestem při otevření a markery
 * na mapě se vytvoří jednou (clusterer) — pan/zoom už nic nedotahuje ani nepřekresluje.
 * Hledání adresy (Places), geolokace i výřez mapy jen filtrují načtený seznam client-side.
 * Na plném výřezu (celá zem) se seznam nevypisuje — jen mapa a výzva k zpřesnění.
 */
export default function BranchModal(props: Props) {
    const t = useI18n();
    const mapsReady = useGoogleMaps(props.googleMapsApiKey, props.language);

    const [branches, setBranches] = useState<Branch[] | null>(null);
    const [zBoxesHidden, setZBoxesHidden] = useState(false);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    /** střed geo hledání (Places / geolokace) — seznam do 10 km od něj */
    const [origin, setOrigin] = useState<LatLng | null>(null);
    const [userPos, setUserPos] = useState<LatLng | null>(null);
    const [locating, setLocating] = useState(false);
    const [selected, setSelected] = useState<Branch | null>(null);
    const [mobileMap, setMobileMap] = useState(false);
    /** výřez mapy po posunu/zoomu uživatele — řídí jen výpis seznamu, ne mapu */
    const [viewport, setViewport] = useState<{ zoom: number; sw: LatLng; ne: LatLng } | null>(null);
    /** poslední akce uživatele — určuje, co seznam vlevo odráží */
    const [listSource, setListSource] = useState<ListSource>(null);

    const mapElRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const markerByKeyRef = useRef<Map<string, any>>(new Map());
    const clustererRef = useRef<any>(null);
    const userMarkerRef = useRef<any>(null);
    const highlightedMarkerRef = useRef<any>(null);
    const infoWindowRef = useRef<any>(null);
    const selectedRef = useRef<Branch | null>(null);
    /** po výběru drž řádek v seznamu ve view i přes přeřazení po posunu mapy */
    const pendingScrollRef = useRef(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const selectedItemRef = useRef<HTMLLIElement | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    /** programový posun mapy (fitBounds/panTo/zoom na adresu) nemá přepnout seznam na výřez */
    const programmaticMoveRef = useRef(false);

    // jediné načtení dat — všechny pobočky najednou
    useEffect(() => {
        const controller = new AbortController();
        abortRef.current = controller;
        props.api.branches({ deliveryId: props.deliveryId }, controller.signal)
            .then((data) => {
                setBranches(data.branches);
                setZBoxesHidden(data.zBoxesHidden === true);
                setLoading(false);
            })
            .catch((e: unknown) => {
                if ((e as Error).name !== 'AbortError') {
                    setBranches([]);
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [props.api, props.deliveryId]);

    /** Geolokace: úspěch → seznam v okolí + zoom mapy na uživatele. */
    const requestLocation = useCallback(() => {
        if (!navigator.geolocation) return;

        setLocating(true);
        let finished = false;
        const finish = (position: LatLng | null) => {
            if (finished) return;
            finished = true;
            setLocating(false);
            if (position) {
                setUserPos(position);
                setOrigin(position);
                setListSource('geo');
            }
        };

        // vlastní timeout — prohlížeče umí nechat prompt viset bez odpovědi
        const timer = window.setTimeout(() => finish(null), 6000);

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                window.clearTimeout(timer);
                finish({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            },
            () => {
                window.clearTimeout(timer);
                finish(null);
            },
            { enableHighAccuracy: false, timeout: 5000, maximumAge: 5 * 60 * 1000 },
        );
    }, []);

    // úvodní pokus o polohu — bez souhlasu zůstane jen mapa s výzvou
    useEffect(() => {
        requestLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (props.selectedBranchId != null && selected === null && branches) {
            const current = branches.find((b) => b.id === props.selectedBranchId);
            if (current) setSelected(current);
        }
    }, [branches, props.selectedBranchId, selected]);

    // zámek scrollu + Escape
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') props.onClose();
        };
        document.addEventListener('keydown', onKey);

        return () => {
            document.body.style.overflow = previous;
            document.removeEventListener('keydown', onKey);
            abortRef.current?.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Google Places našeptávač nad polem hledání
    useEffect(() => {
        if (!mapsReady || !searchInputRef.current) {
            return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            // našeptávat jen adresy ze zvolené země doručení
            ...(props.countryCode ? { componentRestrictions: { country: props.countryCode.toLowerCase() } } : {}),
        });
        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const location = place?.geometry?.location;
            if (location) {
                // vybraná adresa se propíše do inputu (widget mění jen DOM, ne React state)
                setQuery(place.formatted_address ?? place.name ?? searchInputRef.current?.value ?? '');
                setOrigin({ lat: location.lat(), lng: location.lng() });
                setListSource('geo');
            }
        });

        return () => {
            listener.remove();
        };
    }, [mapsReady, props.countryCode]);

    /**
     * Seznam vlevo — vždy jen výřez načtených dat podle poslední akce uživatele:
     * psaní = fulltext, výběr adresy/geolokace = okolí do 10 km, pohyb mapy = výřez.
     * Plný výpis tisíců řádků se nikdy nerenderuje.
     */
    const list = useMemo((): { items: Branch[]; capped: boolean; empty: boolean } => {
        if (!branches) return { items: [], capped: false, empty: false };

        const needle = fold(query.trim());
        if (listSource === 'text' && needle.length >= 2) {
            const matches = branches.filter((b) =>
                fold(`${b.name} ${b.subtitle ?? ''} ${b.address.join(' ')}`).includes(needle));

            return { items: matches.slice(0, LIST_LIMIT), capped: matches.length > LIST_LIMIT, empty: matches.length === 0 };
        }

        if (listSource === 'geo' && origin) {
            const nearby = branches
                .map((b) => ({ ...b, distance: distanceKm(origin, b) }))
                .filter((b) => b.distance <= GEO_RADIUS_KM)
                .sort((a, b) => a.distance - b.distance);

            // do 10 km nic (uživatel mimo zemi trhu) — místo prázdného seznamu výzva
            return { items: nearby.slice(0, GEO_LIST_LIMIT), capped: false, empty: nearby.length === 0 };
        }

        if (listSource === 'map' && viewport && viewport.zoom >= VIEWPORT_LIST_MIN_ZOOM) {
            const center = {
                lat: (viewport.sw.lat + viewport.ne.lat) / 2,
                lng: (viewport.sw.lng + viewport.ne.lng) / 2,
            };
            const inView = branches
                .filter((b) => b.lat >= viewport.sw.lat && b.lat <= viewport.ne.lat
                    && b.lng >= viewport.sw.lng && b.lng <= viewport.ne.lng)
                .sort((a, b) => distanceKm(center, a) - distanceKm(center, b));

            return { items: inView.slice(0, LIST_LIMIT), capped: inView.length > LIST_LIMIT, empty: false };
        }

        return { items: [], capped: false, empty: false };
    }, [branches, query, origin, viewport, listSource]);

    const showList = list.items.length > 0
        || (listSource === 'text' && list.empty)
        || (listSource === 'map' && viewport !== null && viewport.zoom >= VIEWPORT_LIST_MIN_ZOOM);

    selectedRef.current = selected;
    // refs pro DOM obsah InfoWindow — applyHighlight je stabilní callback
    const onConfirmRef = useRef(props.onConfirm);
    onConfirmRef.current = props.onConfirm;
    const tRef = useRef(t);
    tRef.current = t;

    /** Zvýraznění vybrané pobočky na mapě: zelená ikona + InfoWindow s názvem a adresou. */
    const applyHighlight = useCallback((branch: Branch | null) => {
        const map = mapRef.current;
        if (!map) return;

        if (highlightedMarkerRef.current) {
            highlightedMarkerRef.current.setIcon(MARKER_ICON);
            highlightedMarkerRef.current.setZIndex(undefined);
            highlightedMarkerRef.current = null;
        }
        infoWindowRef.current?.close();
        if (!branch) return;

        const marker = markerByKeyRef.current.get(`${branch.deliveryId}-${branch.id}`);
        if (marker) {
            marker.setIcon(MARKER_ICON_SELECTED);
            marker.setZIndex(1001);
            highlightedMarkerRef.current = marker;
        }

        // obsah přes DOM (textContent) — názvy/adresy nesmí do innerHTML
        const content = document.createElement('div');
        content.className = 'dpr-map-info';
        const name = document.createElement('strong');
        name.textContent = branch.name;
        content.appendChild(name);
        if (branch.address.length > 0) {
            const address = document.createElement('div');
            address.textContent = branch.address.join(', ');
            content.appendChild(address);
        }
        const selectBtn = document.createElement('button');
        selectBtn.type = 'button';
        selectBtn.className = 'btn btn--custom-1 dpr-map-info__select';
        selectBtn.textContent = tRef.current('Select branch');
        selectBtn.addEventListener('click', () => onConfirmRef.current(branch));
        content.appendChild(selectBtn);

        if (!infoWindowRef.current) {
            infoWindowRef.current = new window.google.maps.InfoWindow({
                pixelOffset: new window.google.maps.Size(0, -30),
                disableAutoPan: true,
            });
        }
        infoWindowRef.current.setContent(content);
        infoWindowRef.current.setPosition({ lat: branch.lat, lng: branch.lng });
        infoWindowRef.current.open({ map });
    }, []);

    // mapa + markery: vytvoří se JEDNOU nad všemi pobočkami, pak už se nepřekreslují
    useEffect(() => {
        if (!mapsReady || !mapElRef.current || !branches || branches.length === 0) {
            return;
        }

        if (!mapRef.current) {
            mapRef.current = new window.google.maps.Map(mapElRef.current, {
                zoom: 7,
                center: { lat: branches[0].lat, lng: branches[0].lng },
                mapTypeControl: false,
                streetViewControl: false,
                zoomControl: true,
                zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_BOTTOM },
                fullscreenControl: true,
                cameraControl: false,
            });

            // pan/zoom → jen aktualizace výřezu pro seznam (žádný fetch, žádné překreslení markerů);
            // ruční pohyb mapy (vč. kliku na cluster) navíc přepne seznam na výřez
            mapRef.current.addListener('idle', () => {
                const b = mapRef.current?.getBounds();
                if (!b) return;
                const ne = b.getNorthEast();
                const sw = b.getSouthWest();
                setViewport({
                    zoom: mapRef.current.getZoom(),
                    sw: { lat: sw.lat(), lng: sw.lng() },
                    ne: { lat: ne.lat(), lng: ne.lng() },
                });
                if (programmaticMoveRef.current) {
                    programmaticMoveRef.current = false;
                } else {
                    pendingScrollRef.current = false;
                    setListSource('map');
                }
            });
        }

        let cancelled = false;
        void loadMarkerClusterer().then((ClustererClass) => {
            if (cancelled || !mapRef.current) return;

            const bounds = new window.google.maps.LatLngBounds();
            markerByKeyRef.current = new Map();
            markersRef.current = branches.map((branch) => {
                const marker = new window.google.maps.Marker({
                    position: { lat: branch.lat, lng: branch.lng },
                    title: branch.name,
                    icon: MARKER_ICON,
                });
                marker.addListener('click', () => setSelected(branch));
                bounds.extend(marker.getPosition());
                markerByKeyRef.current.set(`${branch.deliveryId}-${branch.id}`, marker);

                return marker;
            });

            if (ClustererClass && markersRef.current.length > 0) {
                // stejná ikonografie jako legacy googleMap.js
                clustererRef.current = new ClustererClass(mapRef.current, markersRef.current, {
                    imagePath: '/images/markerclusterer/m',
                    maxZoom: 14,
                });
            } else {
                markersRef.current.forEach((m) => m.setMap(mapRef.current));
            }

            // pobočka předvybraná z dřívějška — markery vznikají async, doznač ji
            if (selectedRef.current) {
                applyHighlight(selectedRef.current);
            } else {
                programmaticMoveRef.current = true;
                mapRef.current.fitBounds(bounds);
            }
        });

        return () => {
            cancelled = true;
            clustererRef.current?.clearMarkers();
            clustererRef.current = null;
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = [];
            markerByKeyRef.current = new Map();
            infoWindowRef.current?.close();
        };
    }, [mapsReady, branches, applyHighlight]);

    // modrá tečka polohy uživatele
    useEffect(() => {
        if (!mapsReady || !mapRef.current || !userPos) return;

        userMarkerRef.current?.setMap(null);
        userMarkerRef.current = new window.google.maps.Marker({
            position: userPos,
            map: mapRef.current,
            zIndex: 1000,
            clickable: false,
            icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
            },
        });
    }, [mapsReady, userPos]);

    // geo hledání → přiblížit mapu na hledané místo
    useEffect(() => {
        if (origin && mapRef.current) {
            programmaticMoveRef.current = true;
            mapRef.current.setCenter(origin);
            mapRef.current.setZoom(13);
        }
    }, [origin]);

    // vybraná pobočka → zelený marker + InfoWindow, přiblížit mapu, odscrollovat seznam
    useEffect(() => {
        if (!selected) return;
        pendingScrollRef.current = true;
        applyHighlight(selected);
        if (mapRef.current) {
            programmaticMoveRef.current = true;
            mapRef.current.panTo({ lat: selected.lat, lng: selected.lng });
            if (mapRef.current.getZoom() < SELECT_ZOOM) {
                mapRef.current.setZoom(SELECT_ZOOM);
            }
        }
    }, [selected, applyHighlight]);

    // drž vybraný řádek ve view — i po přeřazení seznamu (posun mapy po panTo/zoomu);
    // pending shazuje ruční pohyb mapy, aby seznam neskákal zpět při prohlížení
    useEffect(() => {
        if (pendingScrollRef.current) {
            selectedItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selected, list]);

    const renderBranch = (branch: Branch) => {
        const isSelected = selected?.id === branch.id && selected?.deliveryId === branch.deliveryId;

        return (
            <li key={`${branch.deliveryId}-${branch.id}`} ref={isSelected ? selectedItemRef : undefined}>
                <button
                    type="button"
                    className={`dpr-branch${isSelected ? ' dpr-branch--selected' : ''}`}
                    onClick={() => setSelected(branch)}
                >
                    <span className="dpr-branch__logo">
                        {(branch.logoUrl ?? props.fallbackLogoUrl)
                            ? <img src={branch.logoUrl ?? props.fallbackLogoUrl ?? ''} alt="" loading="lazy" />
                            : null}
                    </span>
                    <span className="dpr-branch__text">
                        <span className="dpr-branch__name">{branch.name}</span>
                        {branch.subtitle ? <span className="dpr-branch__subtitle">{branch.subtitle}</span> : null}
                        {branch.distance != null ? (
                            <span className="dpr-branch__distance">📍 {branch.distance < 10 ? branch.distance.toFixed(1) : Math.round(branch.distance)} km</span>
                        ) : null}
                    </span>
                    <span className={`dpr-branch__price${branch.free ? ' dpr-branch__price--free' : ''}`}>
                        {branch.free ? t('zdarma') : branch.price}
                    </span>
                </button>
                {isSelected ? (
                    <button
                        type="button"
                        className="btn btn--custom-1 dpr-branch__select"
                        disabled={props.confirming}
                        onClick={() => props.onConfirm(branch)}
                    >
                        {props.confirming ? <Spinner className="dpr-btn-spinner" /> : null}
                        <span>{t('Select branch')}</span>
                    </button>
                ) : null}
            </li>
        );
    };

    return (
        <div className="dpr-modal" role="dialog" aria-modal="true" aria-label={props.title}>
            <div className="dpr-modal__backdrop" onClick={props.onClose} />
            <div className="dpr-modal__dialog">
                <div className="dpr-modal__header">
                    <div className="dpr-modal__title">{props.title}</div>
                    <button type="button" className="dpr-modal__close" onClick={props.onClose} aria-label="×">×</button>
                </div>

                <div className="dpr-modal__search">
                    <label htmlFor="dpr-branch-search">
                        {t('Enter a postcode, city or full address to find your nearest branch:')}
                    </label>
                    <div className="dpr-modal__searchrow">
                        <input
                            id="dpr-branch-search"
                            ref={searchInputRef}
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            value={query}
                            onChange={(e) => {
                                const value = e.target.value;
                                setQuery(value);
                                setOrigin(null);
                                setListSource(value.trim().length >= 2 ? 'text' : null);
                            }}
                        />
                        <button
                            type="button"
                            className="dpr-modal__locate"
                            onClick={requestLocation}
                            disabled={locating}
                            title={t('Použít moji polohu')}
                            aria-label={t('Použít moji polohu')}
                        >
                            {locating ? <Spinner className="dpr-btn-spinner" /> : '📍'}
                        </button>
                    </div>
                    {zBoxesHidden ? (
                        <p className="alert alert-warning dpr-modal__note">
                            {t('Z-BOXy nejsou pro tento košík k dispozici – některý produkt se do nich nevejde.')}
                        </p>
                    ) : null}
                </div>

                <div className={`dpr-modal__content${mobileMap ? ' dpr-modal__content--map' : ''}`}>
                    <div className="dpr-modal__list">
                        {loading ? (
                            <div className="dpr-modal__loading">
                                <Spinner />
                                <div className="dpr-skeleton-rows">
                                    {[0, 1, 2, 3, 4].map((i) => <div key={i} className="dpr-skeleton dpr-skeleton--row" />)}
                                </div>
                            </div>
                        ) : showList ? (
                            <>
                                {list.capped ? (
                                    <p className="alert alert-info">{t('The number of search results is too large. Specify the assignment.')}</p>
                                ) : null}
                                {list.items.length === 0 ? (
                                    <p className="alert alert-info">{t('No branches matches the name entered.')}</p>
                                ) : null}
                                <ul className="dpr-branches">
                                    {list.items.map(renderBranch)}
                                </ul>
                            </>
                        ) : (
                            <>
                                {selected ? <ul className="dpr-branches">{renderBranch(selected)}</ul> : null}
                                <p className="alert alert-info dpr-modal__hint">
                                    {mapsReady
                                        ? t('Zadejte adresu výše nebo přibližte mapu a vyberte výdejní místo.')
                                        : t('Zadejte adresu výše a vyberte nejbližší výdejní místo.')}
                                </p>
                            </>
                        )}
                    </div>
                    {mapsReady ? <div className="dpr-modal__map" ref={mapElRef} /> : null}
                </div>

                <div className="dpr-modal__footer">
                    {mapsReady ? (
                        <button type="button" className="btn btn-default dpr-modal__switch" onClick={() => setMobileMap(!mobileMap)}>
                            {mobileMap ? t('Close map') : t('Map')}
                        </button>
                    ) : <span />}
                    <button
                        type="button"
                        className="btn btn--custom-1 dpr-modal__confirm"
                        disabled={!selected || props.confirming}
                        onClick={() => selected && props.onConfirm(selected)}
                    >
                        {props.confirming ? <Spinner className="dpr-btn-spinner" /> : null}
                        <span>{t('Select branch')}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
